import { alpha } from '@mui/material';
import { CSSObject, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { getGlassTintStyles, SUPPORTS_BACKDROP_FILTER } from 'src/constants/glass';

// Shared shell for the composer banners (suggested reply, scheduled message); `token` is the
// per-banner hue that tints the glass and colours the ink.
export const getComposerBannerStyles = (theme: Theme, token: string): CSSObject => {
  const glass = getGlassTintStyles(theme, token);

  return {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    backgroundColor: glass.background,
    color: token,
    backdropFilter: glass.backdropFilter,
    WebkitBackdropFilter: glass.WebkitBackdropFilter,
    border: glass.border,
    [SUPPORTS_BACKDROP_FILTER]: glass.supportsFallback,
  };
};

// Shared metrics for composer banner actions (AI suggestion header, scheduled-message banner)
export const getBannerActionSx = (theme: Theme): SystemStyleObject<Theme> => ({
  minWidth: 0,
  minHeight: 'auto',
  gap: theme.spacing(0.625),
  padding: theme.spacing(0.5, 1.125),
  borderRadius: '0.4375rem',
  fontSize: '0.65625rem',
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.05em',
  color: 'inherit',
  '&:hover': {
    backgroundColor: 'color-mix(in srgb, currentColor 14%, transparent)',
  },
});

// CSSObject, not SystemStyleObject: this is spread into `styled()` callbacks, whose return
// type has no `variants` key — the sx-flavoured type fails to typecheck there.
export const privateNoteButtonStyles = (theme: Theme): CSSObject => {
  const noteColor = theme.palette.custom.OV.extended.Signature.color;
  // Light theme fills with the amber container (mockup) instead of the brown `noteButton`.
  const isLight = theme.palette.mode === 'light';
  const noteButtonBackground = isLight
    ? theme.palette.custom.OV.extended.Signature.colorContainer
    : theme.palette.custom.OV.custom.InternalNotes.noteButton;
  const noteButtonHoverBackground = isLight
    ? `color-mix(in srgb, ${theme.palette.custom.OV.extended.Signature.colorContainer} 88%, ${noteColor})`
    : theme.palette.custom.OV.custom.InternalNotes.noteButtonHover;

  return {
    // :not(.Mui-disabled) keeps the send button's own muted disabled fill — equal specificity,
    // so without it whichever emotion class was injected last wins.
    '& .MuiButton-containedPrimary:not(.Mui-disabled)': {
      backgroundColor: noteButtonBackground,
      ...(isLight && { color: noteColor }),
      '&:hover': {
        backgroundColor: noteButtonHoverBackground,
        // The send group's own `filter` hover lands instantly while this fill animates over
        // 250ms — together they read as a flash that snaps back.
        filter: 'none',
      },
    },
    // Every tools-row action in note mode (Snooze, formatting icons, bubble menu), not just
    // Send — `!important` beats their own component-level `color: 'inherit'`.
    '& .MuiButton-textPrimary, & .MuiButton-outlinedPrimary': {
      color: `${noteColor} !important`,
      '&:hover': {
        backgroundColor: `${alpha(noteColor, 0.12)} !important`,
      },
    },
  };
};
